import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trophy, Swords, ChevronDown, ChevronUp, Ticket } from "lucide-react";
import { GoldCoin, FollisCoin } from "@/components/CoinIcons";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, Empty, btn } from "@/components/Page";
import Modal from "@/components/Modal";
import BattleReplay from "@/components/BattleReplay";
import { TACTICS, SKILL_META } from "@/lib/game";

function isMine(ownerId, currentUserId) {
  return !!ownerId && !!currentUserId && ownerId === currentUserId;
}

function highlightNameClass(mine, winner) {
  if (mine) return winner ? "text-amber-200 font-semibold" : "text-amber-300 font-semibold";
  return winner ? "text-amber-300" : "text-zinc-500";
}

function highlightLanistaClass(mine) {
  return mine
    ? "text-amber-300 hover:text-amber-200 underline decoration-dotted underline-offset-2"
    : "text-sky-400/80 hover:text-sky-300 underline decoration-dotted underline-offset-2";
}

function countdown(iso) {
  if (!iso) return "";
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return "now";
  const d = Math.floor(ms / 86400000), h = Math.floor((ms % 86400000) / 3600000), m = Math.floor((ms % 3600000) / 60000);
  if (d > 0) return `${d}d ${h}h`;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function Tactics({ value, onChange, prefix = "tour" }) {
  return (
    <div className="grid grid-cols-3 gap-2 my-3">
      {TACTICS.map((t) => (
        <button key={t.id} type="button" data-testid={`${prefix}-tactic-${t.id}`} onClick={() => onChange(t.id)}
          className={`px-2 py-2 text-xs border text-center transition-colors ${value === t.id ? "border-amber-500 bg-amber-900/20 text-amber-300" : "border-zinc-800 text-zinc-400 hover:border-amber-700/50"}`}>
          <div className="font-cinzel uppercase tracking-wide">{t.label}</div>
        </button>
      ))}
    </div>
  );
}

function BracketCard({ b, phase, onEnter, onView, onEditTactics, onBracketTactics, expanded, currentUserId }) {
  const navigate = useNavigate();
  const totalEntrants = b.instances.reduce((s, i) => s + i.entrant_count, 0);
  const champs = b.instances.filter((i) => i.champion);
  return (
    <div data-testid={`bracket-${b.key}`} className={b.highlight
      ? "border-2 border-amber-500/70 bg-gradient-to-br from-amber-950/30 to-zinc-950 p-4 shadow-[0_0_20px_rgba(245,158,11,0.15)] relative"
      : "border border-amber-900/30 bg-zinc-950 p-4"}>
      {b.highlight && (
        <span className="absolute -top-2.5 left-4 bg-amber-500 text-zinc-950 text-[10px] font-cinzel uppercase tracking-widest px-2 py-0.5 rounded-sm">★ Featured Event</span>
      )}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-cinzel text-amber-300 tracking-wide text-lg">{b.name} Bracket</h3>
          <p className="text-xs text-zinc-500">Total skill: <span className="text-zinc-300">{b.range}</span></p>
          {b.last_champions && b.last_champions.length > 0 && (
            <div data-testid={`last-champs-${b.key}`} className="text-[11px] text-amber-400/90 mt-1 flex items-start gap-1 flex-wrap leading-relaxed">
              <Trophy size={11} className="text-amber-400 shrink-0 mt-0.5" />
              <span>
                {b.last_champions.length > 1 ? "Last week’s champions: " : "Last week’s champion: "}
                {b.last_champions.map((c, i) => (
                  <span key={c.glad_id} className="inline-flex items-center gap-1 flex-wrap">
                    {i > 0 && <span className="text-zinc-500 mr-1">,</span>}
                    {c.is_ai ? (
                      <span data-testid={`last-champ-ai-${b.key}-${c.glad_id}`} className="text-amber-300">{c.glad_name}</span>
                    ) : (
                      <button data-testid={`last-champ-link-${b.key}-${c.glad_id}`} onClick={() => navigate(`/gladiator/${c.glad_id}`)}
                        className={`${isMine(c.user_id, currentUserId) ? "text-amber-100 font-semibold" : "text-amber-300"} hover:text-amber-200 underline underline-offset-2`}>
                        {c.glad_name}
                      </button>
                    )}
                    {c.user_name && c.user_id && (
                      <>
                        <span className="text-zinc-600">by</span>
                        <button data-testid={`last-champ-lanista-${b.key}-${c.glad_id}`} onClick={() => navigate(`/profile/${c.user_id}`)}
                          className={`${isMine(c.user_id, currentUserId) ? "text-amber-300 font-semibold" : "text-zinc-400"} hover:text-amber-300 underline underline-offset-2`}>
                          {c.user_name}
                        </button>
                      </>
                    )}
                    {c.user_name && !c.user_id && <span className="text-zinc-600">by {c.user_name}</span>}
                  </span>
                ))}
              </span>
            </div>
          )}
        </div>
        <span className="flex items-center gap-2 shrink-0">
          {b.alliance_free && (
            <span data-testid={`alliance-free-badge-${b.key}`} className="flex items-center gap-1 text-sky-300 text-[11px] font-cinzel uppercase tracking-wide border border-sky-700/50 bg-sky-900/30 px-1.5 py-0.5 rounded-sm">
              <Ticket size={11} /> alliance free
            </span>
          )}
          {!b.alliance_free && b.free_entries > 0 && (
            <span data-testid={`free-badge-${b.key}`} className="flex items-center gap-1 text-emerald-300 text-[11px] font-cinzel uppercase tracking-wide border border-emerald-700/50 bg-emerald-900/30 px-1.5 py-0.5 rounded-sm">
              <Ticket size={11} /> {b.free_entries}× free
            </span>
          )}
          {b.alliance_free
            ? <span className="text-emerald-300 text-sm font-cinzel">Free</span>
            : <span className="flex items-center gap-1 text-amber-400 text-sm"><GoldCoin size={13} /> {b.fee}</span>}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-zinc-500">
        <span>{totalEntrants} entered{b.instances.length > 1 ? ` · ${b.instances.length} tournaments` : ""}</span>
        {b.my_entry && <span className="border border-amber-500/60 bg-amber-950/20 px-2 py-0.5 text-amber-200 shadow-[0_0_16px_rgba(245,158,11,0.12)]" data-testid={`my-entry-${b.key}`}>You: {b.my_entry.glad_name} ({b.my_entry.tactics})</span>}
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-xs text-amber-300/90 border-t border-amber-900/20 pt-2" data-testid={`award-${b.key}`}>
        <Trophy size={12} className="text-amber-400" />
        {b.trophy_only ? (
          <span>Champion wins a permanent <span className="text-amber-300 font-semibold">Par Aequa</span> trophy — glory only, no gold</span>
        ) : (
          <>
            <span>Champion wins a permanent trophy</span>
            <span className="text-zinc-600">+</span>
            <span className="flex items-center gap-1 text-amber-400"><GoldCoin size={12} /> {b.payout ?? b.fee * 10} gold</span>
          </>
        )}
      </div>
      <div className="mt-3 flex gap-2">
        {phase === "signups" && !b.my_entry && (
          <button data-testid={`enter-${b.key}`} className={btn() + " py-1.5 px-4 text-sm"} onClick={() => onEnter(b)}>Enter</button>
        )}
        {phase !== "signups" && !b.my_entry && (
          <span data-testid={`closed-${b.key}`} className="text-xs text-zinc-500 italic py-1.5">Sign-ups closed for this week</span>
        )}
        {b.my_entry?.status === "finished" && (
          <span data-testid={`finished-status-${b.key}`} className="text-xs text-emerald-400 italic py-1.5">Tournament complete — view bracket</span>
        )}
        {phase === "signups" && b.my_entry?.status === "signups" && <span className="text-xs text-zinc-500 italic py-1.5">Entered — good luck Sunday!</span>}
        {phase === "locked" && b.my_entry?.status === "locked" && (
          <span className="text-xs text-amber-400/80 italic py-1.5">
            {b.my_entry.status === "cancelled" ? (
              <span className="text-red-400">Instance #{b.my_entry.instance} cancelled</span>
            ) : (
              <>Bracket drawn — fights Sunday 8 AM</>
            )}
          </span>
        )}
        {phase === "signups" && b.my_entry?.status === "signups" && (
          <button data-testid={`edit-tactics-${b.key}`} className={btn("ghost") + " py-1.5 px-3 text-sm"} onClick={() => onEditTactics(b)}>Change tactics</button>
        )}
        {phase === "locked" && b.my_entry && (() => {
          const isCancelled = b.my_entry.status === "cancelled";
          console.log(`[${b.name}] Status: ${b.my_entry.status}, Cancelled: ${isCancelled}`);
          return !isCancelled;
        })() && (
          <button data-testid={`bracket-tactics-${b.key}`} className={btn() + " py-1.5 px-3 text-sm"} onClick={() => onBracketTactics(b)}>Set bracket tactics</button>
        )}
        {b.instances.length > 0 && (
          <button data-testid={`view-${b.key}`} className={btn("ghost") + " py-1.5 px-3 text-sm flex items-center gap-1"} onClick={() => onView(b.key)}>
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Bracket{b.instances.length > 1 ? "s" : ""}
          </button>
        )}
      </div>
      {champs.length > 0 && (
        <div className="mt-2 text-xs text-amber-400 flex items-center gap-1" data-testid={`champ-${b.key}`}>
          <Trophy size={13} /> {champs.map((i) => b.key === "par_aequa" ? (i.champion.user_name || i.champion.glad_name) : i.champion.glad_name).join(", ")} — champion{champs.length > 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
}

function InstanceBracket({ inst, onBattle, bracketKey, currentUserId }) {
  const navigate = useNavigate();
  return (
    <div className="border border-zinc-800 bg-black/30 p-3 mb-2" data-testid={`instance-${inst.id}`}>
      <div className="flex items-center justify-between mb-2 text-xs">
        <span className="text-zinc-400">Tournament #{inst.instance} · {inst.entrant_count} fighters · <span className="text-amber-400">{inst.fee} gold</span> · <span className="uppercase text-amber-500">{inst.status}</span></span>
        {inst.champion && (
          <span className={`${isMine(inst.champion.user_id, currentUserId) ? "border border-amber-500/70 bg-amber-950/30 px-2 py-1 shadow-[0_0_18px_rgba(245,158,11,0.18)]" : ""} text-amber-400 flex items-center gap-1 rounded-sm`}>
            <Trophy size={12} /> {bracketKey === "par_aequa" ? (inst.champion.user_name || inst.champion.glad_name) : inst.champion.glad_name}
          </span>
        )}
      </div>
      {inst.status === "signups" ? (
        <p className="text-xs text-zinc-600">Sign-ups open — bracket runs Sunday 8 AM (Central).</p>
      ) : inst.status === "cancelled" ? (
        <p className="text-xs text-zinc-600">Cancelled — not enough sign-ups (entries refunded).</p>
      ) : (
        <div className="space-y-2">
          {inst.status === "locked" && (
            <p className="text-xs text-amber-400/80 mb-1">Bracket drawn — study the field and set your tactics. Fights begin Sunday 8 AM (Central).</p>
          )}
          {inst.rounds.map((rnd, ri) => (
            <div key={ri}>
              <div className="text-[10px] uppercase tracking-widest text-zinc-600 mb-1">
                {ri === inst.rounds.length - 1 && inst.champion ? "Final" : `Round ${ri + 1}`}
              </div>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {rnd.map((m, mi) => {
                  const aMine = isMine(m.a_owner_id, currentUserId);
                  const bMine = isMine(m.b_owner_id, currentUserId);
                  const matchMine = aMine || bMine;
                  return (
                  <button key={mi} disabled={!m.battle_id} onClick={() => m.battle_id && onBattle(m.battle_id, ri === inst.rounds.length - 1 && !!inst.champion)}
                    data-testid={`match-${inst.id}-${ri}-${mi}`}
                    className={`text-left text-xs px-2 py-1.5 border ${matchMine ? "border-amber-500/80 bg-amber-950/25 shadow-[0_0_18px_rgba(245,158,11,0.15)]" : m.battle_id ? "border-zinc-800 hover:border-amber-700/50 cursor-pointer" : "border-zinc-900 cursor-default"} ${matchMine && m.battle_id ? "hover:border-amber-300/80" : ""} transition-colors`}>
                    {m.bye ? (
                      <>
                        <span className={aMine ? "text-amber-100 font-semibold" : "text-amber-300"}>{m.a}</span>
                        {m.a_owner_name && (
                          <span className="text-zinc-600"> · <Link data-testid={`match-lanista-${m.a_id}`}
                            to={`/profile/${m.a_owner_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className={highlightLanistaClass(aMine)}>{m.a_owner_name}</Link></span>
                        )}
                        <span className="text-zinc-600 italic"> — advances (bye)</span>
                      </>
                    ) : (
                      <>
                        <span className={highlightNameClass(aMine, m.winner === m.a)}>{m.a}</span>
                        {m.a_owner_name && (
                          <span className="text-zinc-600"> · <Link data-testid={`match-lanista-${m.a_id}`}
                            to={`/profile/${m.a_owner_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className={highlightLanistaClass(aMine)}>{m.a_owner_name}</Link></span>
                        )}
                        <span className="text-zinc-700"> vs </span>
                        <span className={highlightNameClass(bMine, m.winner === m.b)}>{m.b}</span>
                        {m.b_owner_name && (
                          <span className="text-zinc-600"> · <Link data-testid={`match-lanista-${m.b_id}`}
                            to={`/profile/${m.b_owner_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className={highlightLanistaClass(bMine)}>{m.b_owner_name}</Link></span>
                        )}
                        {m.winner && <span className="text-zinc-600"> · ▶</span>}
                      </>
                    )}
                  </button>
                );})}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TournamentsPanel() {
  const { refreshUser, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [mine, setMine] = useState([]);
  const [enterB, setEnterB] = useState(null);
  const [editB, setEditB] = useState(null);
  const [bracketB, setBracketB] = useState(null);
  const [mdefault, setMdefault] = useState("balanced");
  const [mtactics, setMtactics] = useState({});
  const [openOpp, setOpenOpp] = useState({});
  const [glad, setGlad] = useState("");
  const [tactics, setTactics] = useState("balanced");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [battle, setBattle] = useState(null);
  const [battleShareable, setBattleShareable] = useState(false);
  const PA_SKILLS = ["strength", "speed", "defense", "stamina", "technique", "precision"];
  const PA_CLASSES = ["Provocator", "Dimachaerus", "Retiarius"];
  const PA_PRESETS = {
    Provocator: [
      { name: "Bulwark", desc: "Immovable defense & endurance", skills: { defense: 220, stamina: 200, strength: 170, technique: 150, precision: 130, speed: 130 } },
      { name: "Juggernaut", desc: "Crushing power behind a wall", skills: { strength: 230, defense: 200, stamina: 180, technique: 140, precision: 130, speed: 120 } },
      { name: "Counter Attack", desc: "Punish openings with skill", skills: { technique: 210, defense: 200, precision: 190, stamina: 150, strength: 140, speed: 110 } },
    ],
    Dimachaerus: [
      { name: "Blademaster", desc: "Relentless dual-blade offense", skills: { strength: 220, speed: 200, technique: 180, precision: 160, stamina: 130, defense: 110 } },
      { name: "Duelist", desc: "Precise, technical strikes", skills: { technique: 220, precision: 210, speed: 170, strength: 160, stamina: 130, defense: 110 } },
      { name: "Windrunner", desc: "Evasive speed & footwork", skills: { speed: 230, defense: 180, strength: 170, technique: 150, precision: 140, stamina: 130 } },
    ],
    Retiarius: [
      { name: "Trapper", desc: "Control the net, dictate pace", skills: { technique: 220, precision: 200, speed: 170, defense: 150, stamina: 140, strength: 120 } },
      { name: "Skirmisher", desc: "Fast, precise hit-and-run", skills: { speed: 220, precision: 200, technique: 170, strength: 150, stamina: 140, defense: 120 } },
      { name: "Steadfast", desc: "Balanced endurance fighter", skills: { stamina: 200, defense: 190, strength: 180, technique: 160, speed: 140, precision: 130 } },
    ]};
  const [paB, setPaB] = useState(null);
  const [paChamps, setPaChamps] = useState([]);
  const [legendChamps, setLegendChamps] = useState([]);
  const [view, setView] = useState("tournaments");
  const [paCls, setPaCls] = useState("Provocator");
  const [paName, setPaName] = useState("");
  const [paSkills, setPaSkills] = useState(() => Object.fromEntries(PA_SKILLS.map((s) => [s, 100])));
  const [paTactics, setPaTactics] = useState("balanced");
  const paTotal = Object.values(paSkills).reduce((s, v) => s + (parseInt(v, 10) || 0), 0);
  const setPaSkill = (sk, val) => {
    let v = parseInt(val, 10);
    if (isNaN(v)) v = 0;
    v = Math.max(0, Math.min(300, v));
    setPaSkills((prev) => ({ ...prev, [sk]: v }));
  };
  const clampPaSkill = (sk) => setPaSkills((prev) => ({ ...prev, [sk]: Math.max(100, Math.min(300, parseInt(prev[sk], 10) || 100)) }));
  const applyPreset = (p) => setPaSkills({ ...p.skills });

  const load = () => Promise.all([api.get("/tournaments"), api.get("/gladiators"), api.get("/tournaments/par-aequa/champions"), api.get("/tournaments/legendary/champions")])
    .then(([t, g, c, l]) => { setData(t.data); setMine(g.data.active || []); setPaChamps(c.data.champions || []); setLegendChamps(l.data.champions || []); })
    .catch(() => setData(false));
  useEffect(() => { load(); }, []);

  const eligible = (b) => {
    const lo = typeof b.min === "number" ? b.min : 0;
    const max = typeof b.max === "number" ? b.max : Infinity;
    return mine.filter((g) => {
      const tot = Object.values(g.effective_skills || g.skills || {}).reduce((s, v) => s + v, 0);
      return tot >= lo && tot <= max;
    });
  };

  const openEnter = (b) => {
    if (b.key === "par_aequa") {
      setPaCls("Provocator"); setPaName(""); setPaSkills(Object.fromEntries(PA_SKILLS.map((s) => [s, 100]))); setPaTactics("balanced"); setPaB(b);
      return;
    }
    setEnterB(b); setGlad(""); setTactics("balanced");
  };

  const enterParAequa = async () => {
    if (paName.trim().length < 2) return toast.error("Give your gladiator a name (2–40 characters).");
    if (paTotal !== 1000) return toast.error(`Skills must total exactly 1000 (currently ${paTotal}).`);
    setBusy(true);
    try {
      await api.post("/tournaments/par-aequa/enter", { cls: paCls, skills: paSkills, tactics: paTactics, name: paName.trim() });
      toast.success("Entered Par Aequa! Your challenger awaits Sunday.");
      setPaB(null); await load(); refreshUser();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const openEdit = (b) => { setEditB(b); setTactics(b.my_entry?.tactics || "balanced"); };

  const openBracket = (b) => {
    console.log('Opening bracket tactics for:', b.name);
    console.log('my_entry:', b.my_entry);
    console.log('paths:', b.my_entry?.paths);
    window.lastBracketB = b; // Store for debugging
    setBracketB(b);
    setMdefault(b.my_entry?.tactics || "balanced");
    setMtactics({ ...(b.my_entry?.matchup_tactics || {}) });
    setOpenOpp({});
  };

  const saveBracketTactics = async () => {
    setBusy(true);
    try {
      await api.put("/tournaments/matchup-tactics", { bracket: bracketB.key, default_tactics: mdefault, matchups: mtactics });
      toast.success(`Bracket tactics saved for the ${bracketB.name} bracket.`);
      setBracketB(null); await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const saveTactics = async () => {
    setBusy(true);
    try {
      await api.put("/tournaments/tactics", { bracket: editB.key, tactics });
      toast.success(`Tactics updated for the ${editB.name} bracket.`);
      setEditB(null); await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const signup = async () => {
    if (!glad) return toast.error("Pick a gladiator");
    setBusy(true);
    try {
      const { data: r } = await api.post("/tournaments/signup", { glad_id: glad, tactics });
      toast.success(r.free_entry
        ? `Entered the ${r.bracket_name} bracket using a free entry!`
        : `Entered the ${r.bracket_name} bracket! ${r.fee} gold paid.`);
      setEnterB(null); await load(); refreshUser();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const viewBattle = async (id, shareable = false) => {
    try { const { data: b } = await api.get(`/battles/${id}`); setBattleShareable(shareable); setBattle(b); } catch (e) { toast.error(apiErr(e)); }
  };

  if (data === null) return <Loader />;
  if (data === false) return <Empty label="Couldn't load tournaments." />;

  const phaseText = data.phase === "signups"
    ? `Sign-ups close ${countdown(data.signup_close)} · bracket runs Sunday 8 AM (Central)`
    : data.phase === "locked"
    ? `Sign-ups closed — tournaments begin Sunday 8 AM (in ${countdown(data.start)})`
    : "Tournaments are underway — one round every hour";

  return (
    <div data-testid="tournaments-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5 border border-amber-900/30 bg-gradient-to-r from-amber-950/20 to-zinc-950 px-4 py-3">
        <div>
          <h2 className="font-cinzel text-amber-300 tracking-wide flex items-center gap-2"><Trophy size={18} /> Weekend Tournaments</h2>
          <p className="text-xs text-zinc-500 mt-0.5">{phaseText}</p>
          <p className="text-xs text-amber-400/80 mt-1" data-testid="min-entrants-note">
            Each tournament holds up to {data.max_entrants || 16} gladiators and runs with any even field — 8, 10, 12, 14, or 16. If the field isn&apos;t a full 16, some fighters get a first-round bye. Beginners brackets add Arena AI contenders when needed to reach {data.min_entrants || 8} fighters; other brackets below {data.min_entrants || 8} entrants are refunded. Once a bracket fills to {data.max_entrants || 16}, a new tournament opens automatically.
          </p>
        </div>
        <span data-testid="signup-status-pill" className={`shrink-0 text-[11px] font-cinzel uppercase tracking-widest px-3 py-1.5 rounded-sm border ${
          data.phase === "signups"
            ? "text-emerald-300 border-emerald-700/50 bg-emerald-950/30"
            : "text-zinc-400 border-zinc-700 bg-zinc-900/50"}`}>
          {data.phase === "signups" ? "● Sign-ups open" : "Sign-ups closed"}
        </span>
      </div>

      <div className="flex items-center gap-2 mb-5 border-b border-zinc-800">
        {[["tournaments", "Tournaments"], ["hof", "Tournament HOF"]].map(([id, label]) => (
          <button key={id} data-testid={`tournaments-tab-${id}`} onClick={() => setView(id)}
            className={`px-4 py-2 text-sm font-cinzel uppercase tracking-wide border-b-2 -mb-px transition-colors ${
              view === id ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
            {label}
          </button>
        ))}
      </div>

      {view === "tournaments" && (<>
      {(data.free_entries || []).length > 0 && (
        <div data-testid="free-entries-banner" className="mb-4 flex flex-wrap items-center gap-2 border border-emerald-800/50 bg-emerald-950/20 px-4 py-3 rounded-sm">
          <Ticket size={16} className="text-emerald-400 shrink-0" />
          <span className="text-sm text-emerald-200">You have free tournament entries for:</span>
          {data.free_entries.map((f) => (
            <span key={f.bracket} data-testid={`free-entry-${f.bracket}`}
              className="text-xs font-cinzel uppercase tracking-wide text-emerald-300 border border-emerald-700/50 bg-emerald-900/30 px-2 py-0.5 rounded-sm">
              {f.count}× {f.name}
            </span>
          ))}
          <span className="text-[11px] text-emerald-400/70 w-full sm:w-auto">Won from The Sands — usable by any qualifying gladiator.</span>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {data.brackets.map((b) => (
          <div key={b.key}>
            <BracketCard b={b} phase={data.phase} onEnter={openEnter} onEditTactics={openEdit} onBracketTactics={openBracket}
              onView={(k) => setExpanded((e) => ({ ...e, [k]: !e[k] }))} expanded={expanded[b.key]} currentUserId={user?.user_id} />
            {expanded[b.key] && (
              <div className="mt-1">
                {b.instances.length === 0 ? <p className="text-xs text-zinc-600 px-2 py-2">No tournaments yet.</p>
                  : b.instances.map((inst) => <InstanceBracket key={inst.id} inst={inst} onBattle={viewBattle} bracketKey={b.key} currentUserId={user?.user_id} />)}
              </div>
            )}
          </div>
        ))}
      </div>
      </>)}

      {view === "hof" && (
        <div data-testid="tournament-hof" className="space-y-6">
          {/* Legendary Hall of Champions */}
          <div data-testid="legendary-hall" className="border-2 border-amber-500/40 bg-gradient-to-br from-amber-950/20 to-zinc-950 p-4">
            <h3 className="font-cinzel text-amber-300 tracking-wide text-lg flex items-center gap-2"><Trophy size={18} className="text-amber-400" /> Legendary — Hall of Champions</h3>
            <p className="text-xs text-zinc-500 mb-3">Victors of the top bracket — the mightiest gladiators to ever take the sands.</p>
            {legendChamps.length === 0 ? (
              <p className="text-sm text-zinc-600" data-testid="legendary-hall-empty">No Legendary champions crowned yet. Win a Legendary tournament to etch your name here.</p>
            ) : (
              <div className="space-y-2">
                {legendChamps.map((c) => (
                  <div key={c.id} data-testid={`legend-champ-${c.id}`} className="border border-amber-900/30 bg-zinc-950/60 p-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Trophy size={14} className="text-amber-400 shrink-0" />
                        <button data-testid={`legend-champ-glad-${c.id}`} onClick={() => navigate(`/gladiator/${c.glad_id}`)}
                          className="font-cinzel text-amber-300 hover:text-amber-200 underline underline-offset-2">{c.glad_name}</button>
                        <span className="text-[11px] text-zinc-500">of</span>
                        <button data-testid={`legend-champ-lanista-${c.id}`} onClick={() => navigate(`/profile/${c.user_id}`)}
                          className="text-sky-400/90 hover:text-sky-300 underline underline-offset-2 text-sm">{c.user_name}</button>
                        {c.cls && <span className="text-[11px] text-zinc-400 border border-zinc-800 px-1.5 py-0.5 rounded-sm uppercase tracking-wide">{c.cls}</span>}
                        {c.rating != null && <span className="text-[11px] text-amber-400/80 tabular-nums">{c.rating} elo</span>}
                      </div>
                      <span className="text-[11px] text-zinc-600">{c.at ? new Date(c.at).toLocaleDateString() : ""}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {PA_SKILLS.map((sk) => (
                        <span key={sk} className="text-[10px] text-zinc-400 bg-zinc-900 border border-zinc-800 px-1.5 py-0.5 rounded-sm capitalize">
                          {sk.slice(0, 3)} <span className="text-amber-300 tabular-nums font-semibold">{c.skills?.[sk] ?? 0}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Par Aequa Hall of Champions */}
          <div data-testid="par-aequa-hall" className="border-2 border-amber-500/40 bg-gradient-to-br from-amber-950/20 to-zinc-950 p-4">
            <h3 className="font-cinzel text-amber-300 tracking-wide text-lg flex items-center gap-2"><Trophy size={18} className="text-amber-400" /> Par Aequa — Hall of Champions</h3>
            <p className="text-xs text-zinc-500 mb-3">Past victors on equal footing — and the exact 1,000-skill builds that won them glory.</p>
            {paChamps.length === 0 ? (
              <p className="text-sm text-zinc-600" data-testid="par-aequa-hall-empty">No Par Aequa champions crowned yet. Enter and win to claim your place.</p>
            ) : (
              <div className="space-y-2">
                {paChamps.map((c) => (
                  <div key={c.id} data-testid={`pa-champ-${c.id}`} className="border border-amber-900/30 bg-zinc-950/60 p-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Trophy size={14} className="text-amber-400 shrink-0" />
                        <button data-testid={`pa-champ-lanista-${c.id}`} onClick={() => navigate(`/profile/${c.user_id}`)}
                          className="font-cinzel text-amber-300 hover:text-amber-200 underline underline-offset-2">{c.user_name}</button>
                        {c.cls && <span className="text-[11px] text-zinc-400 border border-zinc-800 px-1.5 py-0.5 rounded-sm uppercase tracking-wide">{c.cls}</span>}
                      </div>
                      <span className="text-[11px] text-zinc-600">{c.at ? new Date(c.at).toLocaleDateString() : ""}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {PA_SKILLS.map((sk) => (
                        <span key={sk} className="text-[10px] text-zinc-400 bg-zinc-900 border border-zinc-800 px-1.5 py-0.5 rounded-sm capitalize">
                          {sk.slice(0, 3)} <span className="text-amber-300 tabular-nums font-semibold">{c.skills?.[sk] ?? 0}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <Modal open={!!enterB} onClose={() => setEnterB(null)} title={enterB ? `Enter ${enterB.name} Bracket` : ""}>
        {enterB && (
          <>
            <p className="text-sm text-zinc-400 mb-1">Total skill {enterB.range}. Entry fee: {enterB.alliance_free
              ? <span className="text-sky-300">Free <span className="text-sky-400/70">(alliance perk)</span></span>
              : enterB.free_entries > 0
              ? <span className="text-emerald-300">Free <span className="text-emerald-400/70">({enterB.free_entries}× available)</span></span>
              : <span className="text-amber-400">{enterB.fee} gold</span>}.</p>
            {enterB.alliance_free && (
              <p className="text-xs text-sky-400/80 mb-2 flex items-center gap-1.5"><Ticket size={12} /> Your alliance has unlocked free entries for this bracket — no gold charged.</p>
            )}
            {!enterB.alliance_free && enterB.free_entries > 0 && (
              <p className="text-xs text-emerald-400/80 mb-2 flex items-center gap-1.5"><Ticket size={12} /> A free entry from The Sands will be used — no gold charged.</p>
            )}
            <p className="text-xs text-zinc-600 mb-3">Set tactics now — your fighter runs on autopilot when the bracket fights Sunday.</p>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500">Your Gladiator</label>
            <select data-testid="tour-glad-select" value={glad} onChange={(e) => setGlad(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 mt-1 mb-2 text-zinc-200 focus:border-amber-600 outline-none">
              <option value="">— select —</option>
              {eligible(enterB).map((g) => {
                const tot = Math.round(Object.values(g.effective_skills || g.skills || {}).reduce((s, v) => s + v, 0));
                return <option key={g.id} value={g.id}>{g.name} ({g.cls}, skill {tot})</option>;
              })}
            </select>
            {eligible(enterB).length === 0 && <p className="text-xs text-zinc-600 mb-2">No active gladiators qualify for this bracket.</p>}
            <Tactics value={tactics} onChange={setTactics} />
            <button data-testid="confirm-enter-btn" disabled={busy || !glad || (!enterB.alliance_free && enterB.free_entries <= 0 && data.gold < enterB.fee)} className={btn() + " w-full mt-1"} onClick={signup}>
              <span className="flex items-center justify-center gap-2"><Swords size={15} /> {enterB.alliance_free || enterB.free_entries > 0 ? "Enter for free" : data.gold < enterB.fee ? "Not enough gold" : `Enter for ${enterB.fee} gold`}</span>
            </button>
          </>
        )}
      </Modal>

      <Modal open={!!paB} onClose={() => setPaB(null)} title="Enter Par Aequa">
        {paB && (
          <div data-testid="par-aequa-builder">
            <p className="text-sm text-zinc-400 mb-1">Build a <span className="text-amber-300">temporary gladiator</span> on equal footing. Entry fee <span className="text-amber-400">{paB.fee} gold</span>. Winner earns a <span className="text-amber-300">Par Aequa</span> trophy for their profile.</p>
            <p className="text-xs text-zinc-600 mb-3">This fighter is used only for this tournament, doesn&apos;t appear in your roster, and is deleted afterward. Every skill starts at 100 (max 300); distribute to a total of exactly <b>1,000</b>.</p>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500">Gladiator Name</label>
            <input data-testid="pa-name-input" value={paName} onChange={(e) => setPaName(e.target.value)} maxLength={40}
              placeholder="Name your champion" className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:border-amber-600 outline-none mt-1 mb-3" />
            <label className="text-[11px] uppercase tracking-widest text-zinc-500">Class (sets default equipment)</label>
            <div className="grid grid-cols-3 gap-2 mt-1 mb-3">
              {PA_CLASSES.map((c) => (
                <button key={c} type="button" data-testid={`pa-cls-${c}`} onClick={() => setPaCls(c)}
                  className={`px-2 py-2 text-xs border font-cinzel uppercase tracking-wide transition-colors ${paCls === c ? "border-amber-500 bg-amber-900/20 text-amber-300" : "border-zinc-800 text-zinc-400 hover:border-amber-700/50"}`}>{c}</button>
              ))}
            </div>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500">Recommended Builds — {paCls}</label>
            <div className="grid grid-cols-3 gap-2 mt-1 mb-3">
              {(PA_PRESETS[paCls] || []).map((p) => (
                <button key={p.name} type="button" data-testid={`pa-preset-${p.name}`} onClick={() => applyPreset(p)}
                  className="px-2 py-2 text-left border border-zinc-800 hover:border-amber-600/60 hover:bg-amber-900/10 transition-colors rounded-sm">
                  <div className="font-cinzel text-xs text-amber-300 uppercase tracking-wide">{p.name}</div>
                  <div className="text-[10px] text-zinc-500 leading-tight mt-0.5">{p.desc}</div>
                </button>
              ))}
            </div>
            <div className="space-y-2 mb-2">
              {PA_SKILLS.map((sk) => (
                <div key={sk} className="flex items-center gap-3" data-testid={`pa-skill-row-${sk}`}>
                  <span className="w-24 text-xs capitalize text-zinc-400">{sk}</span>
                  <input type="range" min={100} max={300} value={Math.max(100, Math.min(300, parseInt(paSkills[sk], 10) || 100))} onChange={(e) => setPaSkill(sk, e.target.value)}
                    data-testid={`pa-skill-${sk}`} className="flex-1 accent-amber-500" />
                  <input type="number" min={100} max={300} value={paSkills[sk]} onChange={(e) => setPaSkill(sk, e.target.value)} onBlur={() => clampPaSkill(sk)}
                    data-testid={`pa-skill-num-${sk}`} className="w-16 bg-zinc-900 border border-zinc-800 px-2 py-1 text-sm text-zinc-200 text-right focus:border-amber-600 outline-none" />
                </div>
              ))}
            </div>
            <div data-testid="pa-total" className={`text-sm font-cinzel mb-3 flex justify-between ${paTotal === 1000 ? "text-emerald-400" : "text-amber-400"}`}>
              <span>Total allocated</span><span className="tabular-nums">{paTotal} / 1000</span>
            </div>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500">Default Tactics</label>
            <Tactics value={paTactics} onChange={setPaTactics} />
            <p className="text-[11px] text-zinc-600 mb-2">This is your default tactic. Once sign-ups close on Saturday and the bracket locks, you can set your full tactics for every battle — a specific plan for each opponent.</p>
            <button data-testid="pa-confirm-btn" disabled={busy || paName.trim().length < 2 || paTotal !== 1000 || data.gold < paB.fee} className={btn() + " w-full mt-1"} onClick={enterParAequa}>
              <span className="flex items-center justify-center gap-2"><Swords size={15} /> {paName.trim().length < 2 ? "Name your gladiator" : paTotal !== 1000 ? `Allocate ${1000 - paTotal > 0 ? (1000 - paTotal) + " more" : "fewer"} points` : data.gold < paB.fee ? "Not enough gold" : `Enter for ${paB.fee} gold`}</span>
            </button>
          </div>
        )}
      </Modal>

      <Modal open={!!editB} onClose={() => setEditB(null)} title={editB ? `Change Tactics — ${editB.name} Bracket` : ""}>        {editB && (
          <>
            <p className="text-sm text-zinc-400 mb-1">Entered: <span className="text-emerald-400">{editB.my_entry?.glad_name}</span></p>
            <p className="text-xs text-zinc-600 mb-3">Adjust your fighter&apos;s tactics any time before the bracket locks Sunday.</p>
            <Tactics value={tactics} onChange={setTactics} />
            <button data-testid="confirm-edit-tactics-btn" disabled={busy} className={btn() + " w-full mt-1"} onClick={saveTactics}>
              <span className="flex items-center justify-center gap-2"><Swords size={15} /> Save tactics</span>
            </button>
          </>
        )}
      </Modal>

      <Modal open={!!bracketB} onClose={() => setBracketB(null)} title={bracketB ? `Bracket Tactics — ${bracketB.name}` : ""}>
        {bracketB && (
          <div className="space-y-4 max-h-[68vh] overflow-y-auto pr-1" data-testid="bracket-tactics-modal">
            <p className="text-xs text-zinc-500">
              Set how <span className="text-emerald-400">{bracketB.my_entry?.glad_name}</span> fights each possible opponent on the path to the title.
              Any matchup you leave alone uses your default tactic.
            </p>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-zinc-500">Default tactic</label>
              <Tactics value={mdefault} onChange={setMdefault} prefix="mt-default" />
            </div>
            {(bracketB.my_entry?.paths || []).length === 0 ? (
              <div data-testid="bracket-tactics-no-paths">
                {bracketB.my_entry?.status === "cancelled" ? (
                  <>
                    <p className="text-xs text-zinc-600 mb-2">Your tournament instance (#{bracketB.my_entry.instance}) was cancelled.</p>
                    <p className="text-xs text-zinc-500">This usually happens when there weren't enough entrants to fill a bracket. You can still see other active instances on the main tournaments page.</p>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-zinc-600 mb-2">Bracket paths unavailable.</p>
                    <p className="text-xs text-zinc-500">This can happen if:</p>
                    <ul className="text-xs text-zinc-500 list-disc list-inside ml-2 space-y-1">
                      <li>The bracket draw hasn't completed yet (check back shortly)</li>
                      <li>Your tournament instance has an issue</li>
                    </ul>
                    <p className="text-xs text-amber-400 mt-3">
                      You can still set your default tactic above, which will be used for all matchups.
                    </p>
                  </>
                )}
              </div>
            ) : (
              (bracketB.my_entry?.paths || []).map((rnd) => (
                <div key={rnd.round} data-testid={`mt-round-${rnd.round}`}>
                  <div className="text-[10px] uppercase tracking-widest text-amber-500/80 mb-1 border-t border-amber-900/20 pt-2">
                    {rnd.label}{rnd.opponents.length > 1 ? " · possible opponents" : ""}
                  </div>
                  <div className="space-y-2">
                    {rnd.opponents.map((o) => {
                      const open = !!openOpp[o.glad_id];
                      const maxSk = Math.max(1, ...PA_SKILLS.map((s) => o.skills?.[s] || 0));
                      const top = [...PA_SKILLS].sort((a, c) => (o.skills?.[c] || 0) - (o.skills?.[a] || 0)).slice(0, 2);
                      const isParAequa = bracketB.key === "par_aequa";
                      return (
                      <div key={o.glad_id} className="border border-zinc-800 bg-black/30 p-2" data-testid={`mt-opp-${o.glad_id}`}>
                        {isParAequa ? (
                          <>
                            <button type="button" data-testid={`mt-opp-toggle-${o.glad_id}`}
                              onClick={() => setOpenOpp((m) => ({ ...m, [o.glad_id]: !m[o.glad_id] }))}
                              className="flex items-center gap-1.5 text-xs text-sky-300 hover:text-sky-200 mb-1">
                              {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                              <span className="underline decoration-dotted underline-offset-2">{o.glad_name}</span>
                              {o.cls && <span className="text-zinc-500 no-underline">· {o.cls}</span>}
                            </button>
                            {open && (
                              <div data-testid={`mt-opp-stats-${o.glad_id}`} className="mb-2 rounded-sm border border-zinc-800/80 bg-zinc-950/60 p-2">
                                <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
                                  <span>{o.cls || "Gladiator"}</span>
                                  <span>Total <span className="text-amber-300 tabular-nums">{o.total_skill}</span></span>
                                </div>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                  {PA_SKILLS.map((s) => {
                                    const v = o.skills?.[s] || 0;
                                    const hot = top.includes(s);
                                    return (
                                      <div key={s} className="flex items-center gap-2">
                                        <span className={`w-16 text-[10px] uppercase tracking-wide ${hot ? "text-amber-300" : "text-zinc-500"}`}>{SKILL_META[s]?.label || s}</span>
                                        <span className="flex-1 h-1.5 bg-zinc-800 rounded-sm overflow-hidden">
                                          <span className={`block h-full ${hot ? "bg-amber-500" : "bg-zinc-600"}`} style={{ width: `${Math.round((v / maxSk) * 100)}%` }} />
                                        </span>
                                        <span className={`w-8 text-right text-[11px] tabular-nums ${hot ? "text-amber-200" : "text-zinc-400"}`}>{v}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-xs text-zinc-300 mb-2 font-medium">
                            <a
                              href={`/gladiator/${o.glad_id}`}
                              className="text-sky-400 hover:text-sky-300 underline decoration-dotted underline-offset-2 font-semibold transition-colors"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {o.glad_name}
                            </a>
                            {o.cls && <span className="text-zinc-500"> · {o.cls}</span>}
                          </div>
                        )}
                        <Tactics value={mtactics[o.glad_id] || mdefault}
                          onChange={(v) => setMtactics((m) => ({ ...m, [o.glad_id]: v }))} prefix={`mt-${o.glad_id}`} />
                      </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
            <button data-testid="confirm-bracket-tactics-btn" disabled={busy} className={btn() + " w-full mt-1"} onClick={saveBracketTactics}>
              <span className="flex items-center justify-center gap-2"><Swords size={15} /> Save bracket tactics</span>
            </button>
          </div>
        )}
      </Modal>

      <BattleReplay battle={battle} open={!!battle} onClose={() => setBattle(null)}
        title="Tournament Bout" exhibition={true} shareable={battleShareable} />
    </div>
  );
}
